import { Link } from '@/ui/links/link/Link.client'
import { fromButtonStyleVariant } from '@/ui/links/link/linkVariantUtils'
/* eslint-disable sonarjs/cognitive-complexity */
import { twMerge } from '@/lib/classname/twMerge'
import type { ButtonVariant } from '@/ui/buttons/button-styles/ButtonStyles'
import { buttonVariants } from '@/ui/buttons/button-styles/ButtonStyles'
import { CMSIconLucide } from '@/ui/cms/cms-icon-lucide/CMSIconLucide.server'
import { Illustration } from '@/ui/illustrations/illustration/Illustration'
import { List, type ListSize } from '@/ui/lists/list/List'
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser'
import { Element, Text } from 'html-react-parser'
import sanitizeHtml from 'sanitize-html'

const listSpacing = {
  none: 'gap-0',
  sm: 'gap-0.5',
  md: 'gap-1',
} as const

type ListSpacing = keyof typeof listSpacing

/**
 * Extracts the inner HTML of a list of DOM nodes, preserving the structure of the HTML tags.
 *
 * If `firstLevel` is true, it will remove the top level tag name.
 *
 * If `firstLevel` is false, it will return the inner HTML and preserve the tag name.
 */
function extractInnerHTML(nodes: Element[], firstLevel: boolean = true): string[] {
  return nodes.map((node) => {
    if (node instanceof Text) {
      return (node as Text).data
    }

    if (node.children) {
      const inner = extractInnerHTML(node.children as Element[], false)

      if (firstLevel) {
        return inner.join('')
      } else {
        return `<${node.name}>${inner.join('')}</${node.name}>`
      }
    }

    return ''
  })
}

export type ParseOptionsArgs = {
  replaceLists?: boolean
}

export const getParseOptions = ({
  replaceLists = true,
}: ParseOptionsArgs = {}): HTMLReactParserOptions => ({
  replace: (domNode: DOMNode) => {
    if (domNode instanceof Element && domNode.name === 'a' && domNode.attribs?.href) {
      const href = domNode.attribs.href
      const children = domNode.children[0] as Text
      const className = domNode.attribs.class
      const target = domNode.attribs.target
      const dataVariant = domNode.attribs['data-variant']

      let variant: ButtonVariant = 'link'

      if (buttonVariants.includes(dataVariant as ButtonVariant)) {
        variant = dataVariant as ButtonVariant
      }

      return (
        <Link
          bold
          href={href}
          variant={fromButtonStyleVariant(variant)}
          className={twMerge(className)}
          opensInNewTab={target === '_blank'}
        >
          {children?.data || href}
        </Link>
      )
    }

    if (replaceLists && domNode instanceof Element && domNode.name === 'ul') {
      const dataSize = domNode.attribs['data-size']
      const dataSpacing = domNode.attribs['data-spacing']

      let size: ListSize = 'md'
      let spacing: ListSpacing = 'md'

      if (Object.keys({ sm: true, md: true }).includes(dataSize as ListSize)) {
        size = dataSize as ListSize
      }

      if (Object.keys(listSpacing).includes(dataSpacing as ListSpacing)) {
        spacing = dataSpacing as ListSpacing
      }

      const texts = domNode.children.filter(
        (child): child is Element => child instanceof Element && child.name === 'li',
      )

      const innerHTMLTexts = extractInnerHTML(texts, true)

      return (
        <List size={size} className={listSpacing[spacing]}>
          {innerHTMLTexts.map((item) => (
            <List.Item key={item}>
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item) }} />
            </List.Item>
          ))}
        </List>
      )
    }

    if (domNode instanceof Element && domNode.name === 'checkgreen') {
      return <Illustration name="check-green" className={domNode.attribs.class} />
    }

    if (domNode instanceof Element && domNode.name === 'checkred') {
      return <Illustration name="check-red" className={domNode.attribs.class} />
    }

    if (domNode instanceof Element && domNode.name === 'cmsiconlucide' && domNode.attribs.name) {
      return <CMSIconLucide className={domNode.attribs.class} name={domNode.attribs.name} />
    }
  },
})

export const parseOptions = getParseOptions()
